import classnames from 'classnames';
import { trackEvent } from 'dibs-tracking';
import {
    MORE_ITEMS,
    ITEM_DETAILS,
    ABOUT_SELLER,
    labelMap,
} from '../../helpers/soldPdpRedesignConstants';
import { dibsLazy } from 'dibs-lazy/src/dibsLazy';
import { graphql, useFragment } from 'react-relay';
import slugify from 'dibs-slugify/exports/slugify';
import { FC, ReactNode, useState, useCallback, MouseEventHandler, Suspense } from 'react';
import { useRespUnavailableContext } from '../PdpRespUnavailableContext/PdpRespUnavailableContext';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { TabList } from 'dibs-elements/exports/TabList';
import { useTab } from 'dibs-elements/exports/useTab';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { PdpRespSharedItemDetails } from '../../respShared/PdpRespSharedItemDetails/PdpRespSharedItemDetails';
import { PdpRespSharedStoryTellingTab } from '../../respShared/PdpRespSharedStoryTelling/PdpRespSharedStoryTellingTab';
import {
    PdpSharedStorytellingTrackingWrapper,
    PdpSharedStorytellingTrackingContextProvider,
} from '../../shared/PdpSharedStoryTelling/PdpSharedStoryTellingTracking';

// styles
import styles from './PdpRespUnavailableDetailsTabs.scss';

// types
import { PdpRespUnavailableDetailsTabs_item$key as Item } from './__generated__/PdpRespUnavailableDetailsTabs_item.graphql';
import { PdpRespUnavailableDetailsTabs_user$key as User } from './__generated__/PdpRespUnavailableDetailsTabs_user.graphql';
import { PdpRespUnavailableDetailsTabs_viewer$key as Viewer } from './__generated__/PdpRespUnavailableDetailsTabs_viewer.graphql';

// lazy components
const PdpRespSharedSellerDetails = dibsLazy(
    'PdpUnavailableSellerAccessible',
    () =>
        import(
            /* webpackChunkName: "PdpUnavailableSellerAccessible" */ '../../respShared/PdpRespSharedSellerDetails/PdpRespSharedSellerDetails'
        )
);

const viewerFragment = graphql`
    fragment PdpRespUnavailableDetailsTabs_viewer on Viewer {
        ...PdpRespSharedSellerDetails_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespUnavailableDetailsTabs_user on User {
        ...PdpRespSharedSellerDetails_user
        ...PdpRespSharedItemDetails_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespUnavailableDetailsTabs_item on Item {
        storyTellingModule {
            tabs {
                tabType
                tabTitle
                ...PdpRespSharedStoryTellingTab_storyTellingTab
            }
        }
        seller {
            isAccessible
        }
        ...PdpRespSharedItemDetails_item
        ...PdpRespSharedSellerDetails_item
    }
`;

const CustomTab: FC<{ name: string; dataTn?: string; children: ReactNode }> = ({
    name,
    dataTn,
    children,
}) => {
    const { tabCallbackRef, handleClick, isActive } = useTab(name);
    const onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
        event => {
            handleClick();
            trackEvent(
                {
                    category: 'product',
                    action: 'sold pdp tab clicked',
                    label: labelMap[name],
                },
                event
            );
        },
        [handleClick, name]
    );

    return (
        <li className={styles.navTabItem}>
            <Link
                dataTn={dataTn}
                onClick={onClick}
                linkRef={tabCallbackRef}
                buyerLinkType="linkNoDecoration"
                className={classnames(styles.navTabLink, { [styles.active]: isActive })}
            >
                {children}
            </Link>
        </li>
    );
};

export const PdpRespUnavailableDetailsTabs: FC<{
    item: Item;
    viewer: Viewer;
    user: User | null;
}> = ({ item: itemRef, user: userRef, viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const { currency } = useRespUnavailableContext();

    const [activeTab, setActiveTab] = useState(MORE_ITEMS);
    const tabs = item?.storyTellingModule?.tabs || [];
    const isSellerAccessible = item?.seller?.isAccessible;
    return (
        <PdpSharedStorytellingTrackingContextProvider>
            <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
                <TabList fullWidth customTab indicatorPadding="xSmall">
                    {[
                        [
                            MORE_ITEMS,
                            <CustomTab key={MORE_ITEMS} name={MORE_ITEMS} dataTn="more-items-tab">
                                <FormattedMessage
                                    id="PdpRespUnavailableDetailsTabs.moreItems"
                                    defaultMessage="More Items"
                                />
                            </CustomTab>,
                        ],
                        [
                            ITEM_DETAILS,
                            <CustomTab
                                key={ITEM_DETAILS}
                                name={ITEM_DETAILS}
                                dataTn="item-details-tab"
                            >
                                <FormattedMessage
                                    id="PdpRespUnavailableDetailsTabs.itemDetails"
                                    defaultMessage="Item Details"
                                />
                            </CustomTab>,
                        ],
                        ...tabs.reduce<[string, ReactNode][]>((acc, tab) => {
                            if (tab) {
                                acc.push([
                                    tab.tabTitle,
                                    <CustomTab
                                        key={tab.tabTitle}
                                        name={tab.tabTitle}
                                        dataTn={`${slugify(tab.tabTitle)}-tab`}
                                    >
                                        {tab.tabTitle}
                                    </CustomTab>,
                                ]);
                            }
                            return acc;
                        }, []),
                        isSellerAccessible && [
                            ABOUT_SELLER,
                            <CustomTab
                                key={ABOUT_SELLER}
                                name={ABOUT_SELLER}
                                dataTn="about-the-seller-tab"
                            >
                                <FormattedMessage
                                    id="PdpRespUnavailableDetailsTabs.aboutSeller"
                                    defaultMessage="About the Seller"
                                />
                            </CustomTab>,
                        ],
                    ]}
                </TabList>
                <div className={styles.panels}>
                    <TabPanels renderOnInactive>
                        {[
                            [MORE_ITEMS, null],
                            [
                                ITEM_DETAILS,
                                <PdpRespSharedItemDetails
                                    user={user}
                                    item={item}
                                    currency={currency}
                                    selectedSkuId={null}
                                    dataTn="item-details-tab-content"
                                    showExpandingArea={activeTab === ITEM_DETAILS}
                                />,
                            ],
                            ...tabs.reduce<[string, ReactNode][]>((acc, tab) => {
                                if (tab) {
                                    acc.push([
                                        tab.tabTitle,
                                        <PdpSharedStorytellingTrackingWrapper key={tab.tabTitle}>
                                            <PdpRespSharedStoryTellingTab
                                                storyTellingTab={tab}
                                                clearBackground={false}
                                                showExpandingArea={activeTab === tab.tabTitle}
                                                dataTn={`${slugify(tab.tabTitle)}-tab-content`}
                                            />
                                        </PdpSharedStorytellingTrackingWrapper>,
                                    ]);
                                }
                                return acc;
                            }, []),
                            isSellerAccessible && [
                                ABOUT_SELLER,
                                <Suspense fallback={null}>
                                    <PdpRespSharedSellerDetails
                                        sku={null}
                                        user={user}
                                        item={item}
                                        viewer={viewer}
                                        currency={currency}
                                        dataTn="about-the-seller-tab-content"
                                    />
                                </Suspense>,
                            ],
                        ]}
                    </TabPanels>
                </div>
            </TabProvider>
        </PdpSharedStorytellingTrackingContextProvider>
    );
};
